import * as React from "react";
import { Info } from "lucide-react";

import { MARKET_DATA_UNAVAILABLE_COPY } from "@/constants/marketDataUnavailableCopy";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { MarketDataConnectOption } from "@/types/marketDataAttribution";
import type { MarketDataUnavailableReason } from "@/types/marketDataUnavailable";
import {
  formatExecutionCandleTypeLabel,
  isExecutionCandleTypeDisabledForBarBudget,
  normalizeExecutionCandleType,
} from "@/utils/executionCandleTimeframe";
import {
  formatMarketDataEarliestAvailable,
  isMarketDataHistoryInsufficientReason,
} from "@/utils/marketDataUnavailable";
import { MARKET_DATA_RESOLVE_SOURCE_CTA_LABEL } from "@/utils/marketDataConnectGate";

export type MarketDataUnavailablePanelProps = {
  reason: MarketDataUnavailableReason;
  message: string;
  suggestedCandleTypes?: string[];
  earliestAvailable?: string;
  maxCandles?: number;
  limitBasis?: string;
  currentCandleType?: string;
  barBudgetMinCandleType?: string | null;
  connectOptions?: MarketDataConnectOption[];
  tradeAnalyticsAvailable?: boolean;
  className?: string;
  onPickCandleType?: (timeframe: string) => void;
  /** When no TF picker exists (e.g. inline row chart), explain how to retry. */
  timeframePickerUnavailableHint?: string;
  onChangeDataSource?: () => void;
  onConnectBroker?: (broker: string) => void;
};

function copyForReason(reason: MarketDataUnavailableReason) {
  if (reason === "hyperliquid_history_insufficient") {
    return MARKET_DATA_UNAVAILABLE_COPY.hyperliquid_history_insufficient;
  }
  if (reason === "liquid_history_insufficient") {
    return MARKET_DATA_UNAVAILABLE_COPY.liquid_history_insufficient;
  }
  if (reason === "market_data_connector_required") {
    return MARKET_DATA_UNAVAILABLE_COPY.market_data_connector_required;
  }
  if (reason === "market_data_preference_unavailable") {
    return MARKET_DATA_UNAVAILABLE_COPY.market_data_preference_unavailable;
  }
  if (reason === "market_data_consent_required") {
    return MARKET_DATA_UNAVAILABLE_COPY.market_data_consent_required;
  }
  if (reason === "no_trade_span") {
    return MARKET_DATA_UNAVAILABLE_COPY.no_trade_span;
  }
  return MARKET_DATA_UNAVAILABLE_COPY.default;
}

/** Shared OHLC-unavailable panel — history limits, connector gates, consent. */
export function MarketDataUnavailablePanel({
  reason,
  message,
  suggestedCandleTypes = [],
  earliestAvailable,
  maxCandles,
  limitBasis,
  currentCandleType,
  barBudgetMinCandleType,
  connectOptions = [],
  tradeAnalyticsAvailable,
  className,
  onPickCandleType,
  timeframePickerUnavailableHint,
  onChangeDataSource,
  onConnectBroker,
}: MarketDataUnavailablePanelProps): React.ReactElement {
  const copy = copyForReason(reason);
  const currentNormalized = normalizeExecutionCandleType(currentCandleType);
  const tfLabel =
    formatExecutionCandleTypeLabel(currentCandleType) ||
    formatExecutionCandleTypeLabel(suggestedCandleTypes[0]) ||
    "this timeframe";

  const coarserOptions = React.useMemo(() => {
    const seen = new Set<string>();
    const options: Array<{ value: string; label: string; disabled: boolean }> = [];
    for (const raw of suggestedCandleTypes) {
      const normalized = normalizeExecutionCandleType(raw);
      if (!normalized || seen.has(normalized)) continue;
      seen.add(normalized);
      if (normalized === currentNormalized) continue;
      options.push({
        value: normalized,
        label: formatExecutionCandleTypeLabel(normalized),
        disabled: isExecutionCandleTypeDisabledForBarBudget(
          normalized,
          barBudgetMinCandleType
        ),
      });
    }
    return options;
  }, [
    barBudgetMinCandleType,
    currentCandleType,
    currentNormalized,
    suggestedCandleTypes,
  ]);

  const formattedEarliest = formatMarketDataEarliestAvailable(earliestAvailable);
  const historyLimitFooter = React.useMemo(() => {
    if (!formattedEarliest || !reason.endsWith("_history_insufficient") || !("footer" in copy)) {
      return null;
    }
    if (limitBasis === "hyperliquid_snapshot_candle_cap" && maxCandles) {
      return `Hyperliquid stores up to ${maxCandles.toLocaleString()} candles per interval — native ${tfLabel} from ${formattedEarliest}.`;
    }
    if (limitBasis === "liquid_calibrated_days") {
      return copy.footer(tfLabel, formattedEarliest);
    }
    return copy.footer(tfLabel, formattedEarliest);
  }, [copy, formattedEarliest, limitBasis, maxCandles, reason, tfLabel]);
  const title =
    reason === "hyperliquid_history_insufficient" && "title" in copy
      ? copy.title(tfLabel)
      : copy.title();

  const showConnectorActions =
    reason === "market_data_connector_required" ||
    reason === "market_data_preference_unavailable";

  return (
    <Alert
      variant="info"
      data-market-data-unavailable=""
      data-market-data-unavailable-reason={reason}
      className={cn(
        "font-[family-name:var(--font-primary)] text-sm font-normal",
        className
      )}
    >
      <Info aria-hidden />
      <AlertTitle className="font-[family-name:var(--font-primary)] text-sm font-medium leading-5 text-foreground">
        {title}
      </AlertTitle>
      <AlertDescription className="group-has-[>svg]/alert:col-start-2 space-y-3 font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
        <span className="block">{message}</span>
        {tradeAnalyticsAvailable ? (
          <span className="block text-xs opacity-90">
            Trade stats and lists still work — only OHLC charts need a fix.
          </span>
        ) : null}
        {timeframePickerUnavailableHint &&
        !onPickCandleType &&
        isMarketDataHistoryInsufficientReason(reason) ? (
          <span className="block text-xs opacity-90">
            {timeframePickerUnavailableHint}
          </span>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          {coarserOptions.length > 0 && onPickCandleType
            ? coarserOptions.map((option) => (
                <Button
                  key={option.value}
                  type="button"
                  variant="outline-brand"
                  size="sm"
                  data-font="primary"
                  disabled={option.disabled}
                  className="h-8 px-4 text-[0.75rem] normal-case tracking-normal"
                  onClick={() => onPickCandleType(option.value)}
                >
                  {"ctaCoarserTf" in copy
                    ? copy.ctaCoarserTf(option.value)
                    : `Switch to ${option.label}`}
                </Button>
              ))
            : null}
          {onChangeDataSource &&
          (showConnectorActions ||
            reason.endsWith("_history_insufficient") ||
            reason === "market_data_consent_required") ? (
            <Button
              type="button"
              variant="outline-brand"
              size="sm"
              data-font="primary"
              className="h-8 px-4 text-[0.75rem] normal-case tracking-normal"
              onClick={onChangeDataSource}
            >
              {showConnectorActions
                ? MARKET_DATA_RESOLVE_SOURCE_CTA_LABEL
                : "ctaChangeSource" in copy
                  ? copy.ctaChangeSource
                  : "Change market data source"}
            </Button>
          ) : null}
          {onConnectBroker &&
          !isMarketDataHistoryInsufficientReason(reason) &&
          connectOptions.map((option) =>
            onConnectBroker ? (
              <Button
                key={option.broker}
                type="button"
                variant="outline"
                size="sm"
                data-font="primary"
                className="h-8 px-4 text-[0.75rem] normal-case tracking-normal"
                onClick={() => onConnectBroker(option.broker)}
              >
                {option.label?.trim() || option.broker}
              </Button>
            ) : null
          )}
        </div>
        {historyLimitFooter ? (
          <span className="block text-xs opacity-90">{historyLimitFooter}</span>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}
